import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { AUTH } from '@core/constant/api.constants';
import { CookieVars } from '@core/constant/cookie-storage.constants';
import { BaseService } from './base.service';
import { AppCookieService } from './cookie.service';
import { LocalStorageService } from './local-storage.service';
import { WebsocketService } from './websocket.service';
import { LocalVars } from '@core/constant/local-storage.constants';
import { extractApiErrorMessage } from '@core/helpers/password-policy.validator';

const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please log in again.';

function hasApiErrorDetails(body: unknown): boolean {
  if (typeof body === 'string') {
    return body.trim().length > 0;
  }
  if (!body || typeof body !== 'object') {
    return false;
  }
  const value = body as { error?: unknown; message?: unknown };
  if (typeof value.message === 'string') {
    return value.message.trim().length > 0;
  }
  if (Array.isArray(value.message)) {
    return value.message.some(
      (message) => typeof message === 'string' && message.trim().length > 0,
    );
  }
  return typeof value.error === 'string' && value.error.trim().length > 0;
}

export interface UserProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  profile_image: string | null;
  roles: string[];
  data: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly router = inject(Router);
  private readonly localStorage = inject(LocalStorageService);
  private readonly cookieService = inject(AppCookieService);
  private readonly baseService = inject(BaseService);
  private readonly websocketService = inject(WebsocketService);

  /** In-memory access token — never persisted to storage. */
  private readonly accessToken = signal<string | null>(null);
  /** Set to true once logout starts; used to block stale API traffic. */
  private readonly logoutInProgress = signal<boolean>(false);
  private readonly sessionError = signal<string | null>(null);

  readonly profile = signal<UserProfile | null>(null);

  /** Reactive logged-in state derived from the access token. */
  readonly isLoggedIn = computed(() => this.accessToken() !== null);

  getAccessToken(): string | null {
    const token = this.getCookie(CookieVars.ACCESS_TOKEN);
    return this.accessToken() || token || null;
  }

  getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
    return null;
  }

  isLogoutInProgress(): boolean {
    return this.logoutInProgress();
  }

  setAccessToken(token: string): void {
    this.accessToken.set(token);
    this.sessionError.set(null);
    this.logoutInProgress.set(false);
    this.websocketService.setAccessToken(token);
    this.connectRealtime();
  }

  clearSession(): void {
    this.accessToken.set(null);
    this.profile.set(null);
    this.localStorage.clear();
    this.websocketService.disconnect();
  }

  /** Subscribe to the admin alerts topic for the logged-in user. The user id is read
   *  from the persisted profile in localStorage — written by login/initSession before
   *  this point — so silent refreshes resubscribe without needing an API roundtrip.
   *  The access-token cookie is httpOnly, so we push the in-memory token (received
   *  from the login/refresh response body) into the websocket service directly. */
  private connectRealtime(): void {
    const userId = this.readUserIdFromStorage();
    if (userId === null) return;
    this.websocketService.subscribeToUserAlert(userId);
  }

  private readUserIdFromStorage(): string | null {
    const raw = this.localStorage.getItem(LocalVars.USER);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as { id?: unknown };
      return typeof parsed.id === 'string' ? parsed.id : null;
    } catch {
      return null;
    }
  }

  rememberSessionError(error: unknown): void {
    if (error instanceof HttpErrorResponse && !hasApiErrorDetails(error.error)) {
      this.sessionError.set(SESSION_EXPIRED_MESSAGE);
      return;
    }
    this.sessionError.set(extractApiErrorMessage(error, SESSION_EXPIRED_MESSAGE));
  }

  consumeSessionError(): string | null {
    const message = this.sessionError();
    this.sessionError.set(null);
    return message;
  }

  /**
   * On app init / page reload, attempt silent refresh.
   * The httpOnly refresh cookie persists across reloads.
   * If valid, we get a new access token → user stays logged in.
   */
  async initSession(): Promise<void> {
    if (!this.localStorage.getItem(LocalVars.USER)) {
      return;
    }

    try {
      const response = (await this.baseService.callPostApi(AUTH.REFRESH_TOKEN, {})) as {
        access_token: string;
      };
      // setAccessToken pushes the token into WebsocketService and resubscribes alerts.
      this.setAccessToken(response.access_token);

      await this.loadProfile();
    } catch (error: unknown) {
      // No valid refresh cookie → user must log in
      this.rememberSessionError(error);
      this.clearSession();
    }
  }

  /** Fetch profile from API and store in signal. Call on app init / reload. */
  async loadProfile(): Promise<void> {
    if (!this.isLoggedIn()) return;
    try {
      const response = (await this.baseService.callGetApi(AUTH.PROFILE)) as UserProfile;
      this.profile.set(response);
    } catch {
      // Interceptor handles 401 redirect
    }
  }

  async logout(): Promise<void> {
    this.logoutInProgress.set(true);
    this.sessionError.set(null);
    try {
      await this.baseService.callPostApi(AUTH.LOGOUT, {});
    } catch {
      /* ignore — clear session regardless */
    }
    this.clearSession();
    try {
      await this.router.navigate(['/login']);
    } finally {
      // Logout flow finished; allow public page API calls (e.g., signup bootstrap data).
      this.logoutInProgress.set(false);
    }
  }
}
